import React, { useEffect, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SSEProvider } from "@/contexts/SSEContext";
import { Toaster } from "@/components/ui/sonner";
import { PushNotificationsBootstrap } from "@/components/PushNotificationsBootstrap";
import { SoftphoneProvider } from "@/contexts/SoftphoneContext";
import { SoftphoneWidget } from "@/components/SoftphoneWidget";

// Pages
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
const HomeDashboard = React.lazy(() => import("@/pages/HomeDashboard"));
const OperationsCenterPage = React.lazy(() => import("@/pages/OperationsCenterPage"));
const AnalyticsPage = React.lazy(() => import("@/pages/AnalyticsPage"));
const IncidentDetailPage = React.lazy(() => import("@/pages/IncidentDetailPage"));
const IncidentDeepDive = React.lazy(() => import("@/pages/IncidentDeepDive"));
const IncidentsTable = React.lazy(() => import("@/pages/IncidentsTable"));
const ReportsCenter = React.lazy(() => import("@/pages/ReportsCenter"));
const TeamManagement = React.lazy(() => import("@/pages/TeamManagement"));
const BroadcastsPage = React.lazy(() => import("@/pages/BroadcastsPage"));
const SettingsPage = React.lazy(() => import("@/pages/SettingsPage"));
const RequesterProfile = React.lazy(() => import("@/pages/RequesterProfile"));
const ResponderDashboard = React.lazy(() => import("@/pages/ResponderDashboard"));
const RequesterDashboard = React.lazy(() => import("@/pages/RequesterDashboard"));
const RequesterIncidentDetail = React.lazy(() => import("@/pages/RequesterIncidentDetail"));
// New AI-Powered Pages
const AIInsightsPage = React.lazy(() => import("@/pages/AIInsightsPage"));
const CommunicationHubPage = React.lazy(() => import("@/pages/CommunicationHubPage"));
const LiveTrackingPage = React.lazy(() => import("@/pages/LiveTrackingPage"));
const CallsConsole = React.lazy(() => import("@/pages/CallsConsole"));
const TenantsPage = React.lazy(() => import("@/pages/TenantsPage"));
const SystemRecordsPage = React.lazy(() => import("@/pages/SystemRecordsPage"));

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/login', { replace: true });
    }
  }, [loading, user, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
}

// Role-based dashboard selector
function RoleBasedDashboard() {
  const { activeRole } = useAuth();
  
  // Use key to force re-mount when role changes
  const key = `dashboard-${activeRole}`;
  
  // Redirect to role-specific dashboard
  if (activeRole === 'FIRST_RESPONDER' || activeRole === 'SECOND_RESPONDER') {
    return <ResponderDashboard key={key} />;
  }
  
  if (activeRole === 'REQUESTER') {
    return <RequesterDashboard key={key} />;
  }
  
  // Admin and Command Center see the new Home Dashboard
  return <HomeDashboard key={key} />;
}

function RequireRoles({ roles, children }) {
  const { activeRole } = useAuth();
  const navigate = useNavigate();
  const allowed = roles.includes(activeRole);

  useEffect(() => {
    if (!allowed) {
      navigate('/dashboard', { replace: true });
    }
  }, [allowed, navigate]);

  if (!allowed) return null;
  return children;
}

// App router component
function AppRouter() {
  const operationsRoles = ['ADMIN', 'COMMAND_CENTER'];
  const adminRoles = ['ADMIN'];

  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen text-muted-foreground">Loading…</div>}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <RoleBasedDashboard />
        </ProtectedRoute>
      } />
      <Route path="/operations" element={
        <ProtectedRoute>
          <RequireRoles roles={operationsRoles}>
            <OperationsCenterPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/command" element={
        <ProtectedRoute>
          <RequireRoles roles={operationsRoles}>
            <OperationsCenterPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/dispatch" element={
        <ProtectedRoute>
          <RequireRoles roles={operationsRoles}>
            <OperationsCenterPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/incidents" element={
        <ProtectedRoute>
          <RequireRoles roles={operationsRoles}>
            <IncidentsTable />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <RequireRoles roles={adminRoles}>
            <AnalyticsPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <RequireRoles roles={adminRoles}>
            <ReportsCenter />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute>
          <RequireRoles roles={operationsRoles}>
            <TeamManagement />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/broadcasts" element={
        <ProtectedRoute>
          <RequireRoles roles={adminRoles}>
            <BroadcastsPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/ai-insights" element={
        <ProtectedRoute>
          <RequireRoles roles={adminRoles}>
            <AIInsightsPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/communications" element={
        <ProtectedRoute>
          <RequireRoles roles={operationsRoles}>
            <CommunicationHubPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/calls" element={
        <ProtectedRoute>
          <RequireRoles roles={operationsRoles}>
            <CallsConsole />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/tenants" element={
        <ProtectedRoute>
          <RequireRoles roles={adminRoles}>
            <TenantsPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/records" element={
        <ProtectedRoute>
          <RequireRoles roles={adminRoles}>
            <SystemRecordsPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/tracking" element={
        <ProtectedRoute>
          <RequireRoles roles={operationsRoles}>
            <LiveTrackingPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <RequireRoles roles={['ADMIN', 'COMMAND_CENTER', 'REQUESTER', 'FIRST_RESPONDER', 'SECOND_RESPONDER']}>
            <SettingsPage />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/incident/:id" element={
        <ProtectedRoute>
          <RequireRoles roles={operationsRoles}>
            <IncidentDeepDive />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/requester/:phone" element={
        <ProtectedRoute>
          <RequireRoles roles={operationsRoles}>
            <RequesterProfile />
          </RequireRoles>
        </ProtectedRoute>
      } />
      <Route path="/requester/incidents/:id" element={
        <ProtectedRoute>
          <RequesterIncidentDetail />
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SSEProvider>
          <SoftphoneProvider>
            <BrowserRouter>
              <PushNotificationsBootstrap />
              <AppRouter />
              <SoftphoneWidget />
              <Toaster position="top-right" richColors closeButton />
            </BrowserRouter>
          </SoftphoneProvider>
        </SSEProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
